import { HttpError } from '.';
import { AuthService } from '../AuthService';
import { isHttpError } from './HttpError';
import { IApiExceptionResponse, IHttpObjResponse } from './interfaces';

// import { LocalStorgeVars } from '../core/constants';
// import { sendError } from '../utils/logging';

// interface IHttpRequest {
//   type: 'post' | 'get' | 'put' | 'delete';
//   url: string;
// }

export class HttpService {
  public readonly url: string;
  private authService: AuthService;
  private logoutFunc: () => void;
  private errorLogFunc: (
    error: Error,
    data?: { tags?: string[]; customData?: Record<string, string | number>[] }
  ) => void;
  // private pendingRequests: IHttpRequest[] = [];
  // private retryCount: number = 0;
  constructor(settings: {
    baseUrl: string;
    authService: AuthService;
    logoutFunc: () => void;
    errorLogFunc: (error: Error, data?: { tags?: string[]; customData?: Record<string, string | number>[] }) => void;
  }) {
    this.url = settings.baseUrl;
    this.authService = settings.authService;
    this.logoutFunc = settings.logoutFunc;
    this.errorLogFunc = settings.errorLogFunc;
  }

  public async downloadFile(fileUrl: string, fileName: string = 'test'): Promise<void> {
    const settings: RequestInit = {
      method: 'get'
    };

    const response = await this.executeRequest(fileUrl, settings);

    const blob = await response.blob();

    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(objectUrl);
  }

  public async get(url: string): Promise<Response> {
    const settings: RequestInit = {
      method: 'get'
    };

    return this.executeRequest(url, settings);
  }

  public async getObj<T>(url: string): Promise<IHttpObjResponse<T>> {
    const obj: IHttpObjResponse<T> = {
      exceptionMessage: '',
      object: null,
      ok: false,
      statusCode: -1
    };
    try {
      const response = await this.get(url);
      obj.statusCode = response.status;
      obj.ok = response.ok;

      if (response.ok) {
        obj.object = await response.json();
      }
    } catch (e) {
      obj.exceptionMessage = e.message;
    }

    return obj;
  }

  public async post(url: string, payload: any = ''): Promise<Response> {
    if (typeof payload !== 'string') {
      payload = JSON.stringify(payload);
    }

    const headers: Headers = new Headers();
    headers.append('Content-Type', 'Application/json');

    const settings: RequestInit = {
      body: payload,
      headers,
      method: 'post'
    };

    return this.executeRequest(url, settings);
  }

  public async postFormData(url: string, payload: FormData): Promise<Response> {
    const settings: RequestInit = {
      body: payload,
      method: 'post'
    };

    return this.executeRequest(url, settings);
  }

  public async put(url: string, payload: any): Promise<Response> {
    payload = JSON.stringify(payload);

    const headers: Headers = new Headers();
    headers.append('Content-Type', 'Application/json');

    const settings: RequestInit = {
      body: payload,
      headers,
      method: 'put'
    };

    return this.executeRequest(url, settings);
  }

  public async delete(url: string, payload: any = ''): Promise<Response> {
    if (payload !== '') {
      payload = JSON.stringify(payload);
    }

    const headers: Headers = new Headers();
    headers.append('Content-Type', 'Application/json');

    const settings: RequestInit = {
      body: payload,
      headers,
      method: 'delete'
    };

    return this.executeRequest(url, settings);
  }

  private async executeRequest(url: string, settings: RequestInit): Promise<Response> {
    //const accessToken = sessionStorage.getItem(LocalStorgeVars.ACCESS_TOKEN_KEY);
    const accessToken = await this.authService.getAccessTokenSilent();
    const fullUrl = `${this.url}${url}`;
    let response: Response;

    if (!settings.headers) {
      settings.headers = new Headers();
    }

    (settings.headers as Headers).append('Authorization', `Bearer ${accessToken}`);
    console.log('HttpService.executeRequest', fullUrl, settings);
    response = await fetch(fullUrl, settings);
    console.log('HttpService.executeRequest response', response);

    if (response.ok) {
      return response;
    }

    try {
      throw await this.createHttpError(response);
    } catch (e) {
      let sendError = true;
      const customData: Record<string, string | number>[] = [{ apiEndpoint: url }];

      // if the status code if 401 unauthorized log them out
      if (isHttpError(e)) {
        // tslint:disable-next-line: no-string-literal
        customData.push({ httpStatus: e.status });

        if (e.status === 401) {
          sendError = false;
          this.logoutFunc();
        }
      }

      if (sendError) {
        this.errorLogFunc(e, { tags: ['apiException'], customData });
      }
      throw e;
    }
  }

  private async createHttpError(response: Response): Promise<HttpError> {
    let message: string = 'Unknown error';
    let apiException: IApiExceptionResponse | undefined;

    try {
      apiException = (await response.json()) as IApiExceptionResponse;

      if (apiException.exceptionMessage) {
        message = apiException.exceptionMessage;
      } else if (apiException.message) {
        message = apiException.message;
      }
    } catch (e) {
      // grab any errors with desierializing JSON, we don't need to do anything with them
    }

    return new HttpError(response.status, message, apiException);
  }
}
